import { createClient } from '@supabase/supabase-js'

import { getEnv, type AppEnv } from '@/platform/config/env'

/**
 * Supabase browser client.
 *
 * The browser may use only the project URL and the public anon/publishable
 * key. A service-role key in client code would defeat every RLS policy in the
 * system at once, so {@link assertPublicKey} refuses to build a client from
 * one rather than trusting review to catch it (CLAUDE.md; v1.4 SEC-A/SEC-B;
 * acceptance test AUTH-04).
 *
 * Phase 1 constructs and configures the client. No query, RPC or auth flow is
 * implemented yet — those arrive with the schema in Phase 2.
 */

export class InsecureSupabaseKeyError extends Error {
  constructor(reason: string) {
    super(
      `Refusing to create a browser Supabase client: ${reason}. ` +
        `Use the project anon/publishable key only.`,
    )
    this.name = 'InsecureSupabaseKeyError'
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null

  const payload = parts[1]
  if (payload === undefined || payload.length === 0) return null

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const json = atob(padded)
    const decoded: unknown = JSON.parse(json)
    return typeof decoded === 'object' && decoded !== null
      ? (decoded as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/**
 * Reject keys that are not safe to ship to a browser.
 *
 * Covers both Supabase key formats:
 *   - new-style `sb_secret_…` keys, rejected by prefix;
 *   - legacy JWT keys, rejected when the `role` claim is `service_role`.
 *
 * A key this function cannot classify is allowed through: it is better to let
 * an unrecognized-but-valid publishable key work than to hard-fail on a format
 * that did not exist when this was written. The CI bundle scan is the second
 * layer behind this check.
 */
export function assertPublicKey(key: string): void {
  if (key.startsWith('sb_secret_')) {
    throw new InsecureSupabaseKeyError('the key is a Supabase secret key')
  }

  const claims = decodeJwtPayload(key)
  if (claims !== null && claims.role === 'service_role') {
    throw new InsecureSupabaseKeyError('the key carries the service_role claim')
  }
}

export function createSupabaseClient(env: AppEnv) {
  assertPublicKey(env.VITE_SUPABASE_ANON_KEY)

  return createClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
    auth: {
      // Parent sign-in is Google / magic link (v1.4 decision 5), so the client
      // must be able to pick the session up out of the redirect URL.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  })
}

/** The concrete client type, inferred so it tracks the SDK's generics. */
export type AppSupabaseClient = ReturnType<typeof createSupabaseClient>

let cached: AppSupabaseClient | undefined

export function getSupabaseClient(): AppSupabaseClient {
  cached ??= createSupabaseClient(getEnv())
  return cached
}

/** Test-only hook. */
export function resetSupabaseClient(): void {
  cached = undefined
}
